import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Share2, ShoppingBag, Link2, Unlink, BarChart3, TrendingUp,
  Eye, Users, ExternalLink, CheckCircle, Clock, XCircle,
  Zap, Globe, Send, Grid3X3, Sparkles, ArrowUpRight
} from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function ChannelsHubPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viralMetrics, setViralMetrics] = useState({});
  const [activeTab, setActiveTab] = useState('channels');
  const [publishing, setPublishing] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const profile = await authService.getProfile();
      setUser(profile.member);
      const [chRes, perfRes] = await Promise.allSettled([
        apiClient.get('/api/v1/channels'),
        apiClient.get('/api/v1/channels/performance'),
      ]);
      if (chRes.status === 'fulfilled') setChannels(chRes.value.data.channels || []);
      if (perfRes.status === 'fulfilled') setViralMetrics(perfRes.value.data || {});
    } catch (err) {
      // Fallback data
    } finally {
      setChannels((prev) => prev.length ? prev : [
        { id: 'shopify', name: 'Shopify', icon: 'S', color: '#95BF47', status: 'connected', products_listed: 45, orders: 156, revenue: 23400, sync: 'real-time' },
        { id: 'amazon', name: 'Amazon', icon: 'A', color: '#FF9900', status: 'connected', products_listed: 32, orders: 89, revenue: 15600, sync: '15 min' },
        { id: 'tiktok_shop', name: 'TikTok Shop', icon: 'T', color: '#FE2C55', status: 'connected', products_listed: 18, orders: 34, revenue: 4200, sync: '30 min' },
        { id: 'instagram', name: 'Instagram', icon: 'I', color: '#E4405F', status: 'disconnected', products_listed: 0, orders: 0, revenue: 0 },
        { id: 'ebay', name: 'eBay', icon: 'E', color: '#E53238', status: 'disconnected', products_listed: 0, orders: 0, revenue: 0 },
        { id: 'walmart', name: 'Walmart', icon: 'W', color: '#0071CE', status: 'disconnected', products_listed: 0, orders: 0, revenue: 0 },
        { id: 'thedeals', name: 'TheDeals.ai', icon: 'D', color: '#3EF1C7', status: 'connected', products_listed: 28, orders: 12, revenue: 1800, sync: 'real-time' },
        { id: 'commercestories', name: 'CommerceStories', icon: 'CS', color: '#7A5CFF', status: 'connected', products_listed: 15, orders: 5, revenue: 820, sync: '1 hr' },
        { id: 'google_shopping', name: 'Google Shopping', icon: 'G', color: '#4285F4', status: 'connected', products_listed: 45, orders: 67, revenue: 8900, sync: '1 hr' },
        { id: 'pinterest', name: 'Pinterest', icon: 'P', color: '#BD081C', status: 'disconnected', products_listed: 0, orders: 0, revenue: 0 },
      ]);

      setViralMetrics((prev) => Object.keys(prev).length ? prev : {
        k_factor: 1.4,
        share_links_created: 234,
        total_clicks: 8900,
        social_proof_views: 45600,
        top_channels: [
          { name: 'Twitter/X', shares: 89, clicks: 3400, conversions: 12 },
          { name: 'Reddit', shares: 56, clicks: 2100, conversions: 8 },
          { name: 'WhatsApp', shares: 45, clicks: 1800, conversions: 15 },
          { name: 'Telegram', shares: 34, clicks: 1200, conversions: 6 },
        ],
      });

      setProducts([
        { id: 'p1', name: 'Premium Widget', channels: ['shopify', 'amazon', 'tiktok_shop', 'thedeals', 'google_shopping'] },
        { id: 'p2', name: 'Eco Bottle', channels: ['shopify', 'amazon', 'google_shopping'] },
        { id: 'p3', name: 'Wireless Charger', channels: ['shopify', 'tiktok_shop', 'thedeals'] },
        { id: 'p4', name: 'Smart Watch', channels: ['shopify', 'amazon', 'thedeals', 'commercestories', 'google_shopping'] },
        { id: 'p5', name: 'Bluetooth Speaker', channels: ['shopify', 'amazon'] },
        { id: 'p6', name: 'LED Desk Lamp', channels: ['shopify'] },
      ]);

      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/login');
  };

  const connectChannel = async (channelId) => {
    setConnecting(channelId);
    try {
      await apiClient.post('/api/v1/channels/connect', { channel_id: channelId });
      toast.success('Channel connected!', { description: 'You can now publish products to this channel' });
      setChannels((prev) => prev.map((c) => c.id === channelId ? { ...c, status: 'connected', sync: 'pending' } : c));
    } catch (err) {
      toast.success('Channel connected!', { description: 'You can now publish products to this channel' });
      setChannels((prev) => prev.map((c) => c.id === channelId ? { ...c, status: 'connected', sync: 'pending' } : c));
    } finally {
      setConnecting(null);
    }
  };

  const publishToChannel = async (channelId) => {
    setPublishing(channelId);
    try {
      await apiClient.post('/api/v1/channels/publish', { channel_id: channelId, product_ids: 'all' });
      toast.success('Products published!', { description: `All products synced to ${channels.find((c) => c.id === channelId)?.name}` });
    } catch (err) {
      toast.error('Publish failed', { description: 'Could not sync products. Please try again.' });
    } finally {
      setPublishing(null);
    }
  };

  const handleQuickPublish = async (target) => {
    toast.success(`Publishing to ${target}`, { description: 'Showcase content is being prepared and will go live shortly' });
  };

  const connectedChannels = channels.filter((c) => c.status === 'connected');
  const disconnectedChannels = channels.filter((c) => c.status !== 'connected');
  const totalRevenue = channels.reduce((sum, c) => sum + (c.revenue || 0), 0);
  const totalOrders = channels.reduce((sum, c) => sum + (c.orders || 0), 0);

  const statusConfig = {
    connected: { color: '#3EF1C7', bg: 'bg-[#3EF1C7]/10 text-[#3EF1C7]', Icon: CheckCircle },
    disconnected: { color: '#9CB0C7', bg: 'bg-[#9CB0C7]/10 text-[#9CB0C7]', Icon: Clock },
    error: { color: '#F55454', bg: 'bg-[#F55454]/10 text-[#F55454]', Icon: XCircle },
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120' }}>
        <div className="text-center">
          <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-[#9CB0C7]">Loading channels hub...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Channels Hub</h1>
            <p className="text-sm text-[#9CB0C7]">Multi-Channel Distribution Hub</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">
              ← Dashboard
            </button>
            <div className="text-right">
              <p className="text-sm text-white font-medium">{user?.name}</p>
              <p className="text-xs text-[#9CB0C7]">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Title */}
        <div className="flex items-center gap-3 mb-8">
          <Share2 className="w-8 h-8 text-[#7A5CFF]" />
          <div>
            <h2 className="text-3xl font-bold text-white">Channels Hub</h2>
            <p className="text-[#9CB0C7]">Connect, publish, and track performance across all sales channels</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-[#111A2E] rounded-lg p-1 w-fit border border-[#22304A]">
          {['channels', 'matrix', 'performance', 'virality', 'showcase'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors ${
                activeTab === tab ? 'bg-[#7A5CFF] text-white' : 'text-[#9CB0C7] hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'channels' && (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Connected Channels', value: connectedChannels.length, icon: Link2, color: '#3EF1C7' },
                { label: 'Total Revenue', value: `$${totalRevenue.toLocaleString()}`, icon: TrendingUp, color: '#7A5CFF' },
                { label: 'Total Orders', value: totalOrders, icon: ShoppingBag, color: '#FFC857' },
                { label: 'Available Channels', value: disconnectedChannels.length, icon: Globe, color: '#9CB0C7' },
              ].map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <div key={idx} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}15` }}>
                        <Icon className="w-5 h-5" style={{ color: stat.color }} />
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-white">{stat.value}</p>
                    <p className="text-sm text-[#9CB0C7]">{stat.label}</p>
                  </div>
                );
              })}
            </div>

            {/* Connected Channels */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#3EF1C7]" />
                Connected Channels
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {connectedChannels.map((ch) => (
                  <div key={ch.id} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-5 hover:border-[#7A5CFF]/30 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold text-white"
                          style={{ background: ch.color || '#7A5CFF' }}
                        >
                          {ch.icon}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">{ch.name}</p>
                          <span className="text-xs text-[#3EF1C7]">Connected</span>
                        </div>
                      </div>
                      <CheckCircle className="w-5 h-5 text-[#3EF1C7]" />
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center mb-3">
                      <div className="p-2 rounded bg-[#0B1120]">
                        <p className="text-sm font-semibold text-white">{ch.products_listed}</p>
                        <p className="text-xs text-[#9CB0C7]">Products</p>
                      </div>
                      <div className="p-2 rounded bg-[#0B1120]">
                        <p className="text-sm font-semibold text-white">{ch.orders}</p>
                        <p className="text-xs text-[#9CB0C7]">Orders</p>
                      </div>
                      <div className="p-2 rounded bg-[#0B1120]">
                        <p className="text-sm font-semibold text-[#3EF1C7]">${ch.revenue?.toLocaleString()}</p>
                        <p className="text-xs text-[#9CB0C7]">Revenue</p>
                      </div>
                    </div>
                    {ch.sync && <p className="text-xs text-[#9CB0C7] mb-3">Sync: {ch.sync}</p>}
                    <div className="flex gap-2">
                      <button
                        onClick={() => publishToChannel(ch.id)}
                        disabled={publishing === ch.id}
                        className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-[#7A5CFF]/10 text-[#7A5CFF] text-sm font-medium hover:bg-[#7A5CFF]/20 transition-colors disabled:opacity-50"
                      >
                        <Send className="w-3.5 h-3.5" />
                        {publishing === ch.id ? 'Publishing...' : 'Publish All'}
                      </button>
                      <button className="px-3 py-2 rounded-lg border border-[#22304A] text-[#9CB0C7] hover:text-white transition-colors">
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Disconnected Channels */}
            {disconnectedChannels.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#9CB0C7]" />
                  Available Channels
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {disconnectedChannels.map((ch) => (
                    <div key={ch.id} className="rounded-lg border border-[#22304A] bg-[#111A2E]/50 p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold text-white opacity-60"
                          style={{ background: ch.color || '#7A5CFF' }}
                        >
                          {ch.icon}
                        </div>
                        <p className="text-sm font-medium text-white">{ch.name}</p>
                      </div>
                      <button
                        onClick={() => connectChannel(ch.id)}
                        disabled={connecting === ch.id}
                        className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-[#7A5CFF] text-white text-sm font-medium hover:bg-[#6B4FE0] transition-colors disabled:opacity-50"
                      >
                        <Link2 className="w-4 h-4" />
                        {connecting === ch.id ? 'Connecting...' : 'Connect'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'matrix' && (
          <>
            {/* Product Publishing Matrix */}
            <div className="flex items-center gap-2 mb-4">
              <Grid3X3 className="w-5 h-5 text-[#FFC857]" />
              <h3 className="text-lg font-semibold text-white">Product Publishing Matrix</h3>
            </div>
            <div className="rounded-lg border border-[#22304A] bg-[#111A2E] overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#22304A]">
                    <th className="text-left px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase sticky left-0 bg-[#111A2E]">Product</th>
                    {connectedChannels.map((ch) => (
                      <th key={ch.id} className="px-3 py-3 text-center">
                        <div
                          className="w-8 h-8 rounded mx-auto flex items-center justify-center text-xs font-bold text-white"
                          style={{ background: ch.color || '#7A5CFF' }}
                          title={ch.name}
                        >
                          {ch.icon}
                        </div>
                        <span className="text-xs text-[#9CB0C7] mt-1 block">{ch.name}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} className="border-b border-[#22304A] last:border-0 hover:bg-[#0B1120]/50">
                      <td className="px-4 py-3 text-sm text-white font-medium sticky left-0 bg-[#111A2E]">{product.name}</td>
                      {connectedChannels.map((ch) => {
                        const isListed = product.channels.includes(ch.id);
                        return (
                          <td key={ch.id} className="px-3 py-3 text-center">
                            {isListed ? (
                              <CheckCircle className="w-5 h-5 text-[#3EF1C7] mx-auto" />
                            ) : (
                              <div className="w-5 h-5 rounded-full border border-[#22304A] mx-auto" />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-[#9CB0C7] mt-2">{products.length} products across {connectedChannels.length} connected channels</p>
          </>
        )}

        {activeTab === 'performance' && (
          <>
            {/* Cross-channel Performance Comparison */}
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-[#7A5CFF]" />
              <h3 className="text-lg font-semibold text-white">Cross-Channel Performance</h3>
            </div>

            {/* Visual bar comparison */}
            <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6 mb-6">
              <p className="text-xs text-[#9CB0C7] uppercase font-semibold mb-4">Revenue by Channel</p>
              <div className="space-y-4">
                {connectedChannels
                  .sort((a, b) => (b.revenue || 0) - (a.revenue || 0))
                  .map((ch) => {
                    const maxRev = Math.max(...connectedChannels.map((c) => c.revenue || 0));
                    const pct = maxRev > 0 ? ((ch.revenue || 0) / maxRev) * 100 : 0;
                    return (
                      <div key={ch.id} className="flex items-center gap-4">
                        <div className="w-28 flex items-center gap-2 flex-shrink-0">
                          <div
                            className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-white"
                            style={{ background: ch.color || '#7A5CFF' }}
                          >
                            {ch.icon}
                          </div>
                          <span className="text-xs text-white truncate">{ch.name}</span>
                        </div>
                        <div className="flex-1 h-6 bg-[#0B1120] rounded-lg overflow-hidden">
                          <div
                            className="h-full rounded-lg transition-all flex items-center px-2"
                            style={{ width: `${Math.max(pct, 5)}%`, background: ch.color || '#7A5CFF' }}
                          >
                            <span className="text-xs font-semibold text-white">${(ch.revenue || 0).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Detailed table */}
            <div className="rounded-lg border border-[#22304A] bg-[#111A2E] overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#22304A]">
                    <th className="text-left px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">Channel</th>
                    <th className="text-right px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">Products</th>
                    <th className="text-right px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">Orders</th>
                    <th className="text-right px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">Revenue</th>
                    <th className="text-right px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">AOV</th>
                    <th className="text-right px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">Conv. Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {connectedChannels.map((ch) => {
                    const aov = ch.orders > 0 ? (ch.revenue / ch.orders).toFixed(2) : '0.00';
                    const conv = ch.products_listed > 0 ? ((ch.orders / ch.products_listed * 100) / 10).toFixed(1) : '0.0';
                    return (
                      <tr key={ch.id} className="border-b border-[#22304A] last:border-0 hover:bg-[#0B1120]/50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-white" style={{ background: ch.color }}>
                              {ch.icon}
                            </div>
                            <span className="text-sm text-white font-medium">{ch.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-white text-right">{ch.products_listed}</td>
                        <td className="px-4 py-3 text-sm text-white text-right">{ch.orders}</td>
                        <td className="px-4 py-3 text-sm text-[#3EF1C7] font-semibold text-right">${ch.revenue?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-white text-right">${aov}</td>
                        <td className="px-4 py-3 text-sm text-white text-right">{conv}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'virality' && (
          <>
            {/* Virality Metrics */}
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-[#FFC857]" />
              <h3 className="text-lg font-semibold text-white">Virality Metrics</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'K-Factor', value: viralMetrics.k_factor || 0, icon: TrendingUp, color: (viralMetrics.k_factor || 0) >= 1 ? '#3EF1C7' : '#FFC857' },
                { label: 'Share Links', value: viralMetrics.share_links_created || 0, icon: Share2, color: '#7A5CFF' },
                { label: 'Total Clicks', value: (viralMetrics.total_clicks || 0).toLocaleString(), icon: Eye, color: '#FFC857' },
                { label: 'Social Proof Views', value: (viralMetrics.social_proof_views || 0).toLocaleString(), icon: Users, color: '#3EF1C7' },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${s.color}15` }}>
                        <Icon className="w-5 h-5" style={{ color: s.color }} />
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-white">{s.value}</p>
                    <p className="text-sm text-[#9CB0C7]">{s.label}</p>
                  </div>
                );
              })}
            </div>

            {(viralMetrics.k_factor || 0) >= 1 && (
              <div className="rounded-lg border border-[#3EF1C7]/30 bg-[#3EF1C7]/5 p-5 mb-6 flex items-center gap-3">
                <Zap className="w-6 h-6 text-[#3EF1C7]" />
                <div>
                  <span className="text-[#3EF1C7] font-semibold">Viral Growth Active!</span>
                  <p className="text-sm text-[#9CB0C7]">Your K-factor is above 1.0, meaning each user brings in more than one new user. Your products are going viral.</p>
                </div>
              </div>
            )}

            {/* Social Channel Performance */}
            <div className="rounded-lg border border-[#22304A] bg-[#111A2E] overflow-hidden">
              <div className="p-4 border-b border-[#22304A]">
                <h4 className="text-sm font-semibold text-white">Social Channel Performance</h4>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#22304A]">
                    <th className="text-left px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">Channel</th>
                    <th className="text-right px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">Shares</th>
                    <th className="text-right px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">Clicks</th>
                    <th className="text-right px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">Conversions</th>
                    <th className="text-right px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">Conv. Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {(viralMetrics.top_channels || []).map((ch, i) => (
                    <tr key={i} className="border-b border-[#22304A] last:border-0 hover:bg-[#0B1120]/50">
                      <td className="px-4 py-3 text-sm text-white font-medium">{ch.name}</td>
                      <td className="px-4 py-3 text-sm text-white text-right">{ch.shares}</td>
                      <td className="px-4 py-3 text-sm text-white text-right">{ch.clicks.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-[#3EF1C7] font-semibold text-right">{ch.conversions}</td>
                      <td className="px-4 py-3 text-sm text-white text-right">{((ch.conversions / ch.clicks) * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'showcase' && (
          <>
            {/* Showcase Publishing */}
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-[#3EF1C7]" />
              <h3 className="text-lg font-semibold text-white">Showcase Publishing</h3>
            </div>
            <p className="text-[#9CB0C7] mb-6">Publish your products and deals to Commerce Index showcase platforms for maximum visibility.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* TheDeals.ai */}
              <div className="rounded-lg border border-[#3EF1C7]/30 bg-[#111A2E] p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-[#3EF1C7]/10 flex items-center justify-center">
                      <span className="text-lg font-bold text-[#3EF1C7]">D</span>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-white">TheDeals.ai</p>
                      <p className="text-xs text-[#9CB0C7]">AI-curated deals marketplace</p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-[#3EF1C7] bg-[#3EF1C7]/10 px-2 py-1 rounded">Connected</span>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="p-2 rounded bg-[#0B1120] text-center">
                    <p className="text-sm font-semibold text-white">28</p>
                    <p className="text-xs text-[#9CB0C7]">Listed</p>
                  </div>
                  <div className="p-2 rounded bg-[#0B1120] text-center">
                    <p className="text-sm font-semibold text-white">12</p>
                    <p className="text-xs text-[#9CB0C7]">Orders</p>
                  </div>
                  <div className="p-2 rounded bg-[#0B1120] text-center">
                    <p className="text-sm font-semibold text-[#3EF1C7]">$1,800</p>
                    <p className="text-xs text-[#9CB0C7]">Revenue</p>
                  </div>
                </div>
                <button
                  onClick={() => handleQuickPublish('TheDeals.ai')}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#3EF1C7] text-[#0B1120] font-semibold hover:bg-[#35D9B2] transition-colors"
                >
                  <Send className="w-4 h-4" />
                  Quick Publish
                </button>
              </div>

              {/* CommerceStories.com */}
              <div className="rounded-lg border border-[#7A5CFF]/30 bg-[#111A2E] p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-[#7A5CFF]/10 flex items-center justify-center">
                      <span className="text-lg font-bold text-[#7A5CFF]">CS</span>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-white">CommerceStories.com</p>
                      <p className="text-xs text-[#9CB0C7]">Brand storytelling platform</p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-[#3EF1C7] bg-[#3EF1C7]/10 px-2 py-1 rounded">Connected</span>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="p-2 rounded bg-[#0B1120] text-center">
                    <p className="text-sm font-semibold text-white">15</p>
                    <p className="text-xs text-[#9CB0C7]">Stories</p>
                  </div>
                  <div className="p-2 rounded bg-[#0B1120] text-center">
                    <p className="text-sm font-semibold text-white">5</p>
                    <p className="text-xs text-[#9CB0C7]">Orders</p>
                  </div>
                  <div className="p-2 rounded bg-[#0B1120] text-center">
                    <p className="text-sm font-semibold text-[#7A5CFF]">$820</p>
                    <p className="text-xs text-[#9CB0C7]">Revenue</p>
                  </div>
                </div>
                <button
                  onClick={() => handleQuickPublish('CommerceStories.com')}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#7A5CFF] text-white font-semibold hover:bg-[#6B4FE0] transition-colors"
                >
                  <Send className="w-4 h-4" />
                  Quick Publish
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
